const SESSION_SYNC_CHANNEL_NAME = "dispatchiq_external_session_sync";
const SESSION_SYNC_TIMEOUT_MS = 4000;
const SESSION_SYNC_ATTEMPTS = 2;
const SESSION_SYNC_RETRY_DELAY_MS = 150;
const DISPATCH_TOKEN_KEY = "oauthToken";

type SessionSnapshot = {
    entries: Record<string, string>;
};

type SessionRequestMessage = {
    type: "session-request";
    requestId: string;
};

type SessionResponseMessage = {
    type: "session-response";
    requestId: string;
    snapshot: SessionSnapshot;
};

type SessionSyncMessage = SessionRequestMessage | SessionResponseMessage;

type BroadcastChannelLike = Pick<BroadcastChannel, "postMessage" | "close"> & {
    addEventListener: (
        type: "message",
        listener: (event: MessageEvent<SessionSyncMessage>) => void
    ) => void;
    removeEventListener: (
        type: "message",
        listener: (event: MessageEvent<SessionSyncMessage>) => void
    ) => void;
};

type ChannelFactory = () => BroadcastChannelLike | null;

function createChannel(): BroadcastChannelLike | null {
    if (
        typeof window === "undefined" ||
        typeof BroadcastChannel === "undefined"
    ) {
        return null;
    }

    return new BroadcastChannel(SESSION_SYNC_CHANNEL_NAME);
}

function createRequestId(): string {
    if (
        typeof crypto !== "undefined" &&
        typeof crypto.randomUUID === "function"
    ) {
        return crypto.randomUUID();
    }

    return `external-session-${Date.now()}-${Math.random()
        .toString(36)
        .slice(2, 10)}`;
}

function isSessionEntryKey(key: string): boolean {
    return key === DISPATCH_TOKEN_KEY;
}

export function collectExternalSessionSnapshot(
    storage: Storage | null | undefined
): SessionSnapshot | null {
    if (!storage) {
        return null;
    }

    const token = storage.getItem(DISPATCH_TOKEN_KEY);
    if (!token) {
        return null;
    }

    return {
        entries: {
            [DISPATCH_TOKEN_KEY]: token,
        },
    };
}

export function applyExternalSessionSnapshot(
    storage: Storage | null | undefined,
    snapshot: SessionSnapshot | null | undefined
): boolean {
    if (!storage || !snapshot) {
        return false;
    }

    const entries = Object.entries(snapshot.entries);
    if (entries.length === 0) {
        return false;
    }

    for (const [key, value] of entries) {
        if (!isSessionEntryKey(key)) {
            continue;
        }

        storage.setItem(key, value);
    }

    return true;
}

export async function requestExternalSessionSnapshot(
    storage: Storage | null | undefined,
    timeoutMs: number = SESSION_SYNC_TIMEOUT_MS,
    channelFactory: ChannelFactory = createChannel
): Promise<boolean> {
    if (!storage) {
        return false;
    }

    for (let attempt = 0; attempt < SESSION_SYNC_ATTEMPTS; attempt++) {
        const channel = channelFactory();
        if (!channel) {
            return false;
        }

        const requestId = createRequestId();
        const applied = await new Promise<boolean>((resolve) => {
            let settled = false;

            const settle = (result: boolean) => {
                if (settled) {
                    return;
                }

                settled = true;
                clearTimeout(timeoutId);
                channel.removeEventListener("message", handleMessage);
                channel.close();
                resolve(result);
            };

            const handleMessage = (event: MessageEvent<SessionSyncMessage>) => {
                const message = event.data;
                if (
                    !message ||
                    message.type !== "session-response" ||
                    message.requestId !== requestId
                ) {
                    return;
                }

                settle(applyExternalSessionSnapshot(storage, message.snapshot));
            };

            const timeoutId = window.setTimeout(
                () => settle(false),
                Math.max(0, timeoutMs)
            );

            channel.addEventListener("message", handleMessage);
            channel.postMessage({
                type: "session-request",
                requestId,
            } satisfies SessionRequestMessage);
        });

        if (applied) {
            return true;
        }

        if (attempt < SESSION_SYNC_ATTEMPTS - 1) {
            await new Promise((resolve) =>
                window.setTimeout(resolve, SESSION_SYNC_RETRY_DELAY_MS)
            );
        }
    }

    return false;
}

export function subscribeToExternalSessionSnapshotRequests(
    getSnapshot: () => SessionSnapshot | null,
    channelFactory: ChannelFactory = createChannel
): () => void {
    const channel = channelFactory();
    if (!channel) {
        return () => {};
    }

    const handleMessage = (event: MessageEvent<SessionSyncMessage>) => {
        const message = event.data;
        if (!message || message.type !== "session-request") {
            return;
        }

        const snapshot = getSnapshot();
        if (!snapshot) {
            return;
        }

        channel.postMessage({
            type: "session-response",
            requestId: message.requestId,
            snapshot,
        } satisfies SessionResponseMessage);
    };

    channel.addEventListener("message", handleMessage);

    return () => {
        channel.removeEventListener("message", handleMessage);
        channel.close();
    };
}
